/**
 * Downloading a pack onto the device, and keeping it fresh.
 *
 * TWO PATHS, ON PURPOSE:
 *
 *  1. `pet_app.api.offline.get_pack` — one call, delta-capable, with tombstones.
 *     See `docs/README.offline-mode-backend-prompt.md` §1. Preferred.
 *  2. Today's endpoints, paged. Costs ~6-10 round trips and cannot learn that a
 *     row was DELETED (a list simply stops mentioning it), so a full refresh is
 *     used to replace each dataset wholesale rather than merge into it.
 *
 * The fallback exists so offline mode is useful before the backend ships
 * anything. The probe result is remembered for the session, so an undeployed
 * endpoint costs one 404, not one per refresh.
 */

import { ref } from "vue";
import { axiosInstance } from "@/plugins/axios";
import {
  GetAllPosBarcodes,
  GetPosCatalogItems,
  GetPosItemGroups,
} from "@/api/posApi";
import { GetCustomersForOfflinePack } from "@/api/customerApi";
import { GetPetBoardingUnits } from "@/api/boardingApi";
import { GetHealthcareVisits } from "@/api/healthcareVisitApi";
import { useCashierStore } from "@/stores/cashier.store";
import useBranchStore from "@/stores/branch.store";
import { isOffline } from "@/services/offlineSignal";
import type { PackId } from "./packs";
import { PACK_DEFINITIONS, readDataset, writeDataset } from "./packs";
import { estimateStorage, OfflineStorageError } from "./idb";
import { currentUserKey } from "./user";
import { activePackScope, NO_TILL_SELECTED } from "./scope";

export { currentUserKey };

/** Pages are small enough to keep one response comfortable on a weak tablet. */
const CATALOG_PAGE_SIZE = 100;

/** A guard against an endpoint that keeps claiming there is more. */
const MAX_CATALOG_PAGES = 60;

/**
 * ⚠️ A PACK DOWNLOAD NEVER READS THROUGH THE READ CACHE.
 *
 * Its only job is to bring the device up to date, and the till reads the pack, not
 * the network — so a cached answer stored here becomes the till's truth until the
 * next download. `Item Group` (15 min) and `Item Price` (45 s, served stale while it
 * revalidates) are both cached, and only the Sync button cleared them first; the
 * timer, focus and reconnect refreshes stored whatever the cache had.
 */
const FRESH_READ = { cache: "bypass" } as const;

/** See docs/README.offline-mode-backend-prompt.md §1. Not deployed yet. */
const PACK_ENDPOINT = "/api/method/pet_app.api.offline.get_pack";

export interface DatasetResult {
  dataset: string;
  rows: number;
  ms: number;
  skipped?: boolean;
  error?: string;
}

export interface PackSyncResult {
  pack: PackId;
  ok: boolean;
  full: boolean;
  datasets: DatasetResult[];
  ms: number;
  error?: string;
}

/** `null` = not probed yet. Remembered per session. */
let backendPackEndpointAvailable: boolean | null = null;

export function resetPackEndpointProbe(): void {
  backendPackEndpointAvailable = null;
}

/**
 * Is the one-call snapshot endpoint deployed?
 *
 * Mirrors `probeDriverOrdersCapability`: a missing method answers 404 or an
 * AttributeError, which is a deployment fact, not a user-visible failure.
 */
async function packEndpointAvailable(): Promise<boolean> {
  if (backendPackEndpointAvailable !== null)
    return backendPackEndpointAvailable;

  try {
    await axiosInstance.get(PACK_ENDPOINT, {
      params: { pack: "pos", probe: 1 },
      headers: {
        "X-Suppress-Error-Toast": "1",
        "X-Suppress-Action-Loader": "1",
        "X-Suppress-Authorization-Banner": "1",
      },
    });

    backendPackEndpointAvailable = true;
  }
  catch (error: any) {
    // A REFUSAL still proves the method exists — only "not found" means undeployed.
    const status = Number(error?.status ?? error?.response?.status ?? 0);
    const message = String(error?.message ?? "");
    const undeployed = status === 404
      || /not found|does not exist|AttributeError|ModuleNotFoundError/i.test(message);

    backendPackEndpointAvailable = !undeployed;

    if (undeployed)
      console.info("Offline pack endpoint is not deployed yet; using the per-doctype fallback.");
  }

  return backendPackEndpointAvailable;
}

async function timed(dataset: string, work: () => Promise<number>): Promise<DatasetResult> {
  const startedAt = Date.now();

  try {
    const rows = await work();

    return { dataset, rows, ms: Date.now() - startedAt };
  }
  catch (error: any) {
    // One dataset failing must not abandon the rest: a till with items but no
    // delivery partners is still a working till.
    return {
      dataset,
      rows: 0,
      ms: Date.now() - startedAt,
      error: String(error?.message ?? error ?? "failed"),
    };
  }
}

// --- POS pack ------------------------------------------------------------------

/**
 * Every sellable item, already priced and stock-checked for THIS till.
 *
 * `GetPosCatalogItems` is reused rather than re-implemented because it already
 * resolves the winning Item Price (stock-UOM match, then currency, then newest)
 * and falls back to `standard_rate` — rules the till depends on and that must not
 * fork between online and offline.
 */
async function downloadPosItems(user: string, warehouse: string | null, scope: string | null): Promise<number> {
  const loading = ref(false);
  const all: any[] = [];
  let total = Number.POSITIVE_INFINITY;

  for (let page = 0; page < MAX_CATALOG_PAGES && all.length < total; page += 1) {
    const result = await GetPosCatalogItems(loading, {
      warehouse: warehouse ?? undefined,
      limit_start: page * CATALOG_PAGE_SIZE,
      limit_page_length: CATALOG_PAGE_SIZE,
      order_by: "item_name asc",
    }, FRESH_READ);

    if (!result)
      throw new Error("The catalogue could not be read.");

    const rows = result.data ?? [];

    all.push(...rows);
    total = Number.isFinite(result.total) && result.total > 0 ? result.total : all.length;

    if (!rows.length)
      break;
  }

  await writeDataset(user, "pos", "items", all, null, scope);

  return all.length;
}

/**
 * The warehouse a sale from THIS TILL actually draws from.
 *
 * ⚠️ NOT `POS Profile.warehouse`, and ⚠️ NOT the signed-in user's branch either.
 *
 * `stamp_branch_and_warehouse` rewrites every invoice row and the header to
 * `Branch.custom_wharehouse`, beating the profile field and anything the client sends:
 * location is a fact about the BRANCH. And a till belongs to `POS Profile.branch`,
 * which is not necessarily the user's own — for an Administrator the user's `current`
 * branch is `null` entirely, so resolving through the user would silently fall back to
 * the profile's warehouse, which is the wrong one on the very till that diverges.
 *
 * Measured on the live site: "Alkokh Vet Store - Main Cashier" advertises
 * "المخزن الرئيسي" while its branch (main) uses "مخزن صالة العيادة". The offline pack
 * stores Bin quantities for ONE warehouse and reserves stock against them, so reading
 * the wrong one makes every quantity a cashier sees about somewhere else.
 *
 * Order: the till's branch warehouse → the user's branch warehouse → the profile's.
 * The last is today's behaviour, so this is never worse and usually right.
 */
async function resolveEffectiveWarehouse(fallback: string | null, posProfile: string | null): Promise<string | null> {
  try {
    const branchStore = useBranchStore();

    if (!branchStore.loaded)
      await branchStore.load();

    // Which branch does this TILL belong to? Read from the profile, not the user.
    let tillBranch: string | null = null;

    if (posProfile) {
      try {
        const response = await axiosInstance.get(
          `/api/resource/POS Profile/${encodeURIComponent(posProfile)}`,
          {
            params: { fields: JSON.stringify(["branch"]) },
            headers: { "X-Suppress-Error-Toast": "1", "X-Suppress-Action-Loader": "1" },
          },
        );

        tillBranch = String(response?.data?.data?.branch ?? "").trim() || null;
      }
      catch {
        // Offline, or the field is absent: fall through to the user's branch.
      }
    }

    const branchWarehouse = branchStore.warehouseForBranch(tillBranch);
    const resolved = branchWarehouse || branchStore.warehouse || fallback;

    if (resolved && resolved !== fallback) {
      console.info(
        "Offline pack: reading stock from the BRANCH warehouse, not the POS profile's",
        { posProfile, tillBranch, branchWarehouse: resolved, profileWarehouse: fallback },
      );
    }

    return resolved;
  }
  catch {
    return fallback;
  }
}

async function downloadPosPack(user: string): Promise<DatasetResult[]> {
  const cashier = useCashierStore();
  const warehouse = await resolveEffectiveWarehouse(cashier.warehouse ?? null, cashier.activeProfileName ?? null);

  /**
   * The till this pack belongs to. Two branches, four tills, four warehouses —
   * data downloaded for one must never be served to another.
   */
  const scope = cashier.activeProfileName ?? null;

  const results: DatasetResult[] = [];

  // The profile decides prices and stock for everything else, so it goes first.
  results.push(await timed("profile", async () => {
    await writeDataset(user, "pos", "profile", [{
      posProfileId: cashier.activeProfileId ?? null,
      posProfileName: cashier.activeProfileName ?? null,
      company: cashier.company ?? null,
      warehouse,
      profileWarehouse: cashier.warehouse ?? null,
      cashAccount: cashier.cashAccount ?? null,
      sellingPriceList: cashier.sellingPriceList ?? null,
      priceListCurrency: cashier.priceListCurrency ?? null,
      currency: cashier.currency ?? null,
      defaultCustomer: cashier.defaultCustomer ?? null,
      defaultModeOfPayment: cashier.defaultModeOfPayment ?? null,
    }], null, scope);

    return 1;
  }));

  results.push(await timed("itemGroups", async () => {
    const groups = await GetPosItemGroups(ref(false), FRESH_READ);

    await writeDataset(user, "pos", "itemGroups", groups ?? [], null, scope);

    return groups?.length ?? 0;
  }));

  results.push(await timed("items", () => downloadPosItems(user, warehouse, scope)));

  results.push(await timed("barcodes", async () => {
    /**
     * ⚠️ THE CODES COME FROM THE ITEMS, NOT FROM `Item Barcode`.
     *
     * That table is EMPTY on this site (confirmed by the backend, 2026-09-26): every code
     * lives on `Item.custom_barcode`, which the catalogue read now requests. Downloading
     * the table anyway produced an empty map and a scanner that silently matched nothing —
     * a miss is indistinguishable from an unknown product, so it would have looked like
     * "offline scanning just doesn't work".
     *
     * `GetAllPosBarcodes` is still consulted afterwards, so a site that DOES populate the
     * table keeps working; it simply adds to what the items already gave us.
     */
    const stored = await readDataset<{ itemCode: string; barcode: string | null }>(user, "pos", "items", scope);
    const rows: Array<{ barcode: string; itemCode: string }> = [];
    const seen = new Set<string>();

    for (const item of stored?.rows ?? []) {
      const barcode = String(item.barcode ?? "").trim();

      if (barcode && !seen.has(barcode)) {
        seen.add(barcode);
        rows.push({ barcode, itemCode: item.itemCode });
      }
    }

    try {
      const extra = await GetAllPosBarcodes();

      for (const [barcode, itemCode] of Object.entries(extra)) {
        if (!seen.has(barcode)) {
          seen.add(barcode);
          rows.push({ barcode, itemCode });
        }
      }
    }
    catch {
      // The dedicated table is optional; the item column is the real source here.
    }

    await writeDataset(user, "pos", "barcodes", rows, null, scope);

    return rows.length;
  }));

  results.push(await timed("customers", async () => {
    const rows = await GetCustomersForOfflinePack();

    await writeDataset(user, "pos", "customers", rows, null, scope);

    return rows.length;
  }));

  results.push(await timed("paymentModes", async () => {
    /**
     * ⚠ A PINIA ARRAY IS A PROXY, AND INDEXEDDB CANNOT STORE ONE.
     *
     * Every other dataset here is plain JSON straight from the API; this one comes
     * from the store, and the structured-clone algorithm rejects the reactive
     * proxy with a DataCloneError. Caught live: the pack reported "1 dataset(s)
     * failed" on every single refresh while the till kept working, because the
     * checkout falls back to a default mode of payment — so the only visible
     * symptom was a warning nobody was reading.
     */
    const modes = [...(cashier.allowedModeOfPayments ?? [])].map(mode => String(mode));

    await writeDataset(user, "pos", "paymentModes", modes, null, scope);

    return modes.length;
  }));

  return results;
}

// --- Packs not yet implemented --------------------------------------------------

/**
 * Clinic, boarding and driver packs land in their own stages (8-10). They are
 * listed here so a doctor's pack is RESOLVED correctly today — the UI can
 * already say "not downloaded" rather than pretending the pack does not exist.
 */
/**
 * The room board, for reading offline.
 *
 * One request, ~35 rows: the whole board, already summarised by the backend. It
 * is NOT scoped to a till — boarding is global, and a stay belongs to the clinic
 * rather than to whichever till the user signed in at.
 *
 * Read-only by design: check-in, check-out and billing are state-machine and money
 * transitions, which `AGENTS.md` forbids queueing offline.
 */
async function downloadBoardingPack(user: string): Promise<DatasetResult[]> {
  const results: DatasetResult[] = [];

  results.push(await timed("board", async () => {
    const units = await GetPetBoardingUnits(ref(false));

    await writeDataset(user, "boarding", "board", units, null, null);

    return units.length;
  }));

  return results;
}

/**
 * Recent visits, for reading offline.
 *
 * ⚠️ ONE PAGE, NOT THE ARCHIVE. 200 rows newest-first covers today and the days
 * around it, which is what a doctor without a connection needs; the full history
 * is thousands of rows and would cost a download nobody is waiting for. The list
 * is stored already mapped, so the screen reads it without a second translation.
 */
async function downloadClinicPack(user: string): Promise<DatasetResult[]> {
  const results: DatasetResult[] = [];

  results.push(await timed("visits", async () => {
    const response = await GetHealthcareVisits(ref(false), {
      limit_start: 0,
      limit_page_length: 200,
      order_by: "visit_datetime desc",
    });

    const rows = response?.data ?? [];

    await writeDataset(user, "clinic", "visits", rows, null, null);

    return rows.length;
  }));

  return results;
}

async function downloadUnimplementedPack(pack: PackId): Promise<DatasetResult[]> {
  return PACK_DEFINITIONS[pack].datasets.map(dataset => ({
    dataset,
    rows: 0,
    ms: 0,
    skipped: true,
  }));
}

// --- Public API -----------------------------------------------------------------

export interface SyncPackOptions {
  /** Ignore stored watermarks and replace every dataset. */
  full?: boolean;
  /** Who the data belongs to. Defaults to the signed-in user. */
  user?: string;
}

export async function syncPack(pack: PackId, options: SyncPackOptions = {}): Promise<PackSyncResult> {
  const startedAt = Date.now();
  const user = options.user ?? currentUserKey();

  if (!user) {
    return {
      pack,
      ok: false,
      full: Boolean(options.full),
      datasets: [],
      ms: 0,
      error: "No signed-in user to download data for.",
    };
  }

  // Nothing to do offline, and attempting it would produce a wall of failures.
  if (isOffline()) {
    return {
      pack,
      ok: false,
      full: Boolean(options.full),
      datasets: [],
      ms: 0,
      error: "offline",
    };
  }

  /**
   * ⚠️ NO TILL, NO POS PACK.
   *
   * The datasets are stamped with the active till and `readDataset` refuses a
   * mismatch, so downloading before one is chosen produced 0.83 MB the till could
   * never read — and reported it as a complete pack. Skipping is not a failure:
   * `prepareOfflineForUser` runs before `cashier.store` has activated a till on a
   * first sign-in, and the watcher in `runtime.ts` downloads the moment it does.
   */
  if (pack === "pos" && !activePackScope("pos")) {
    return {
      pack,
      ok: false,
      full: Boolean(options.full),
      datasets: [],
      ms: 0,
      error: NO_TILL_SELECTED,
    };
  }

  try {
    // Reserved for the one-call endpoint; the fallback is used until it ships.
    await packEndpointAvailable();

    const datasets = pack === "pos"
      ? await downloadPosPack(user)
      : pack === "boarding"
        ? await downloadBoardingPack(user)
        : pack === "clinic"
          ? await downloadClinicPack(user)
          : await downloadUnimplementedPack(pack);

    const failed = datasets.filter(row => row.error);
    const storage = await estimateStorage();

    if (storage.nearlyFull) {
      console.warn("Offline storage is nearly full", {
        usageBytes: storage.usageBytes,
        quotaBytes: storage.quotaBytes,
      });
    }

    return {
      pack,
      ok: failed.length === 0,
      full: true,
      datasets,
      ms: Date.now() - startedAt,
      error: failed.length ? `${failed.length} dataset(s) failed` : undefined,
    };
  }
  catch (error: any) {
    // Storage failures are the one class we must never hide: without them the
    // user is told their data is on the device when it is not.
    const message = error instanceof OfflineStorageError
      ? error.message
      : String(error?.message ?? error ?? "Download failed.");

    return {
      pack,
      ok: false,
      full: Boolean(options.full),
      datasets: [],
      ms: Date.now() - startedAt,
      error: message,
    };
  }
}

export async function syncPacks(packs: PackId[], options: SyncPackOptions = {}): Promise<PackSyncResult[]> {
  const results: PackSyncResult[] = [];

  // Sequential: a tablet on a weak link does better with one request at a time
  // than with four packs competing, and the first pack finishing is what the
  // user is waiting for.
  for (const pack of packs)
    results.push(await syncPack(pack, options));

  return results;
}

/** Has this pack ever been stored for this user? */
export async function packHasData(user: string, pack: PackId): Promise<boolean> {
  for (const dataset of PACK_DEFINITIONS[pack].datasets) {
    const stored = await readDataset(user, pack, dataset);

    if (stored?.rows?.length)
      return true;
  }

  return false;
}

